import { createSelector } from 'reselect';

import { PlatformAppLoader } from '@typings';

import { cacheSelector } from './cache.selectors';

export const platformAppsSelector = createSelector(
  cacheSelector,
  (cache) => cache.platformApps,
);

export const platformAppsLoadersSelector = createSelector(
  platformAppsSelector,
  (platformApps) => {
    type AppendSuffix<T extends string, S extends string> = `${T}${S}`;

    type WithSuffix<T, Suffix extends string> = {
      [K in keyof T as AppendSuffix<K & string, Suffix>]: T[K];
    };

    const handler = <
      Loader extends Record<PlatformAppLoader, boolean>,
      Suffix extends string,
    >(
      loaders: Loader,
      suffix: Suffix,
    ): WithSuffix<Loader, Suffix> =>
      Object.fromEntries(
        Object.entries(loaders).map(([loader, value]) => [
          `${loader}${suffix}`,
          value,
        ]),
      ) as WithSuffix<Loader, Suffix>;

    const { loaders, fetchers } = platformApps;

    return {
      ...handler(loaders, 'Loading'),
      ...handler(fetchers, 'Fetched'),
    };
  },
);

export const platformAppsTemplatesSelector = createSelector(
  platformAppsSelector,
  (platformApps) => platformApps.templates,
);

export const platformAppsInstancesSelector = createSelector(
  platformAppsSelector,
  (platformApps) =>
    /**
     * For demo purposes
     * Temporary
     */
    platformApps.instances.filter(({ state }) => state !== 'uninstalled'),
);

export const platformAppsAppInstancesSelector = (appName: string) =>
  createSelector(platformAppsInstancesSelector, (apps) =>
    apps.filter(({ templateName }) => templateName === appName),
  );

export const platformAppsAppInstanceSelector = (instanceId: string) =>
  createSelector(platformAppsInstancesSelector, (apps) =>
    apps.filter(({ id }) => id === instanceId),
  );

export const platformAppsTemplateSelector = (appName: string) =>
  createSelector(platformAppsTemplatesSelector, (templates) => {
    return templates.find(({ name }) => name === appName);
  });

export const platformAppsCacheSelector = createSelector(
  platformAppsSelector,
  ({ cache }) => cache,
);

export const platformAppsVersionsSelector = createSelector(
  platformAppsCacheSelector,
  ({ versions }) => versions,
);

export const platformAppsTemplatesTagsSelector = createSelector(
  platformAppsCacheSelector,
  ({ templatesTags }) => templatesTags,
);
